import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isToday, parseISO } from 'date-fns';

const DISC_COLORS = {
  sea_kayaking: 'var(--sea)', whitewater: 'var(--ww)', canoe: 'var(--canoe)',
  surfski: 'var(--surfski)', sup: 'var(--sup)', other: 'var(--other)',
};
const DISC_BG = {
  sea_kayaking: 'var(--sea-bg)', whitewater: 'var(--ww-bg)', canoe: 'var(--canoe-bg)',
  surfski: 'var(--surfski-bg)', sup: 'var(--sup-bg)', other: 'var(--other-bg)',
};

function DayDetail({ date, events, onEventClick, onClose, likedIds, onToggleLike }) {
  return (
    <div className="day-detail-overlay" onClick={onClose}>
      <div className="day-detail" onClick={e => e.stopPropagation()}>
        <div className="day-detail-header">
          <div>
            <div className="day-detail-date">{format(date, 'EEEE')}</div>
            <div className="day-detail-full">{format(date, 'MMMM d, yyyy')}</div>
          </div>
          <button className="day-detail-close" onClick={onClose}>✕</button>
        </div>
        <div className="day-detail-list">
          {events.map(opp => (
            <div key={opp.capture_id} className="day-event-row" onClick={() => { onClose(); onEventClick(opp); }}>
              <div style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'8px'}}>
                <div>
                  <div className="day-event-name">{opp.opportunity_name || '—'}</div>
                  <div className="day-event-meta">{opp.outfit_name}{opp.address_city ? ` · ${opp.address_city}` : ''}</div>
                </div>
                <button
                  className={`heart-btn ${likedIds?.has(Number(opp.capture_id)) ? 'heart-liked' : ''}`}
                  onClick={e => { e.stopPropagation(); onToggleLike(opp.capture_id); }}
                  title={likedIds?.has(Number(opp.capture_id)) ? 'Unlike this event' : 'Like this event'}
                >
                  {likedIds?.has(Number(opp.capture_id)) ? '❤️' : '🤍'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Calendar({ opportunities, onEventClick, likedIds, onToggleLike, currentMonth: externalMonth, onMonthChange, undatedCount, onShowUndated }) {
  const [internalMonth, setInternalMonth] = useState(new Date());
  const currentMonth = externalMonth || internalMonth;
  const setCurrentMonth = onMonthChange || setInternalMonth;
  const [selectedDay, setSelectedDay] = useState(null);

  const safeOpps = Array.isArray(opportunities) ? opportunities : [];

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const calStart = startOfWeek(monthStart);
  const calEnd = endOfWeek(monthEnd);

  const days = [];
  let day = calStart;
  while (day <= calEnd) {
    days.push(day);
    day = addDays(day, 1);
  }

  const eventsByDay = {};
  safeOpps.forEach(opp => {
    if (!opp.event_start_date) return;
    try {
      const key = format(parseISO(opp.event_start_date), 'yyyy-MM-dd');
      if (!eventsByDay[key]) eventsByDay[key] = [];
      eventsByDay[key].push(opp);
    } catch {}
  });

  const selectedDayKey = selectedDay ? format(selectedDay, 'yyyy-MM-dd') : null;
  const selectedDayEvents = selectedDayKey ? (eventsByDay[selectedDayKey] || []) : [];
  const DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  return (
    <div className="calendar">
      {!externalMonth && (
        <div className="cal-nav">
          <button className="cal-nav-btn" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>←</button>
          <div className="cal-month-title">{format(currentMonth, 'MMMM yyyy')}</div>
          <div className="cal-nav-right">
            {undatedCount > 0 && (
              <button className="cal-undated-pill" onClick={onShowUndated} title="View undated events">
                {undatedCount} undated
              </button>
            )}
            <button className="cal-nav-btn" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>→</button>
          </div>
        </div>
      )}
      <div className="cal-scroll-wrap">
        <div className="cal-grid">
          {DOW.map(d => (
            <div key={d} className="cal-dow">{d}</div>
          ))}
          {days.map(d => {
            const key = format(d, 'yyyy-MM-dd');
            const dayEvents = eventsByDay[key] || [];
            const inMonth = isSameMonth(d, currentMonth);
            const isTodays = isToday(d);
            return (
              <div
                key={key}
                className={`cal-day ${inMonth ? '' : 'out-month'} ${isTodays ? 'today' : ''} ${dayEvents.length > 0 ? 'has-events' : ''}`}
                onClick={() => { if (dayEvents.length > 0) setSelectedDay(d); }}
              >
                <div className="cal-day-num">{format(d, 'd')}</div>
                <div className="cal-day-events">
                  {dayEvents.slice(0, 3).map(opp => (
                    <div
                      key={opp.capture_id}
                      className="cal-event-chip"
                      style={{ background: DISC_BG[opp.opportunity_class] || DISC_BG.other, color: DISC_COLORS[opp.opportunity_class] || DISC_COLORS.other }}
                      onClick={e => { e.stopPropagation(); onEventClick(opp); }}
                    >
                      {opp.opportunity_name || '—'}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="more-events">+{dayEvents.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {selectedDay && selectedDayEvents.length > 0 && (
        <DayDetail
          date={selectedDay}
          events={selectedDayEvents}
          onEventClick={opp => { setSelectedDay(null); onEventClick(opp); }}
          onClose={() => setSelectedDay(null)}
          likedIds={likedIds}
          onToggleLike={onToggleLike}
        />
      )}
    </div>
  );
}
